import { Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.scss";
import classNames from "classnames/bind";
import ReactPlayer, { Config } from "react-player/lazy";
import { IContentModel } from "models/dto/ZoomiLxp/Models/ContentModels/IContentModel";
import VideoPlaceholder from "assets/images/video_placeholder.png";
import AudioPlaceholder from "assets/images/audio_placeholder.png";
import { ContentTypes } from "models/dto/ZoomiLxp/Utilities/Enumerations/ContentTypes";
import { ControlParams } from "../player-control/player-control";
import { useAppStore } from "store";
import LoadingIndicator from "components/partial/loading-indicator/loading-indicator";
import { isNil } from "lodash";
import { observer } from "mobx-react";
import { ContentAssetType } from "models/dto/ZoomiLxp/Utilities/Enumerations/Content/ContentAssetType";
import { FILE_URL_MAX_AGE_MS, useContentFiles } from "hooks/useContentFiles";

interface PlayerVideoProps {
	params: ControlParams;
	setParams: Dispatch<SetStateAction<ControlParams>>;
	content: IContentModel;
	className?: string;
	children?: ReactNode;
}

interface stateProgress {
	played: number;
	playedSeconds: number;
	loaded: number;
	loadedSeconds: number;
}

const cx = classNames.bind(styles);

const PlayerVideo = (props: PlayerVideoProps) => {
	const { params, className, setParams, children, content } = props;
	const { playing, muted, volume, playerRef, boxRef, handlePlay, handlePause, handleStart, speed, handleComplete } =
		params;

	const { playerStore, contentStore, toasterStore } = useAppStore();

	/* Player ratio: 100 / (1280 / 720) as per https://github.com/cookpete/react-player recommendations */
	const paddingTop = 56.25;
	const [isContentReady, setContentReady] = useState(false);
	const isSessionInitialized = Boolean(contentStore.sessionId);
	const { files, refresh, isStale } = useContentFiles(content);
	const resumeAtRef = useRef<number | null>(null);
	const retriedAtRef = useRef(0);

	const getCurrentTime = () => playerRef?.current?.getCurrentTime() ?? 0;

	const reloadFrom = (position: number) => {
		resumeAtRef.current = position;
		void refresh();
	};

	const reloadIfStale = (position: number) => {
		if (isStale()) reloadFrom(position);
	};

	const zoomParams = useMemo(() => {
		if (playerStore.zoomMultiplier > 1) {
			return {
				width: `${100 * playerStore.zoomMultiplier}%`,
				height: `${100 * playerStore.zoomMultiplier}%`,
				top: `${(1 - playerStore.zoomMultiplier) * 70}%`,
				left: `${(1 - playerStore.zoomMultiplier) * 70}%`,
				paddingTop: `${paddingTop * playerStore.zoomMultiplier}%`,
			};
		}
		return {
			width: "100%",
			height: "100%",
			top: `${(1 - playerStore.zoomMultiplier) * 100}%`,
			left: "0",
			paddingTop: `${paddingTop * playerStore.zoomMultiplier}%`,
		};
	}, [playerStore.zoomMultiplier]);

	const handleDuration = (duration: number) =>
		setParams((prevState: ControlParams) => ({ ...prevState, totalProgress: duration }));

	const handleProgress = (state: stateProgress) => {
		const currentProgress = state.playedSeconds > params.totalProgress ? params.totalProgress : state.playedSeconds;
		setParams((prevState: ControlParams) => ({ ...prevState, currentProgress }));
	};
	const getPoster = () => {
		if (content.contentType === ContentTypes.Video) return VideoPlaceholder;
		else if (content.contentType === ContentTypes.Podcast)
			return !isNil(content.thumbnail) ? content.thumbnail.url : AudioPlaceholder;
	};

	const handleError = (error: any) => {
		if (error?.name === "AbortError") return;
		if (Date.now() - retriedAtRef.current > FILE_URL_MAX_AGE_MS) {
			retriedAtRef.current = Date.now();
			reloadFrom(getCurrentTime());
			return;
		}
		toasterStore.showErrorMessage(`Playback error: ${error?.name ?? ""} ${error?.message ?? ""}`.trim());
	};

	const handleReady = () => {
		if (resumeAtRef.current !== null) {
			playerRef?.current?.seekTo(resumeAtRef.current, "seconds");
			resumeAtRef.current = null;
		}
		setContentReady(true);
		setParams((prevState: ControlParams) => ({ ...prevState, isReady: true }));
	};

	const getPlayOnClick = () => (playing ? handlePause?.() : handlePlay?.());

	const getConfig = () => {
		const configData: Config | undefined = {
			file: {
				attributes: {
					poster: getPoster(),
					controlsList: "nodownload",
					disablePictureInPicture: true,
					preload: "metadata",
					crossOrigin: "anonymous",
				},
			},
		};
		const subtitleTracks = files?.assets
			?.filter((a) => a.assetType === ContentAssetType.Subtitle)
			.map((a) => ({
				kind: "subtitles",
				src: a.url,
				srcLang: a.languageCode,
				label: a.label,
				default: false,
				id: a.uploadedFileId,
			}));

		if (configData && configData.file && subtitleTracks && subtitleTracks.length > 0) {
			configData.file.tracks = [];
			configData.file.tracks = subtitleTracks;
		}
		return configData;
	};

	useEffect(() => {
		playerStore.zoomMultiplier = 1;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (playing) reloadIfStale(getCurrentTime());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [playing]);

	return (
		<div className={cx(styles.player_video, className)} ref={boxRef}>
			<div className={styles.player_video__pause_click_zone} onClick={getPlayOnClick} />
			<LoadingIndicator loading={!isContentReady} backgroundStyle={"none"} spinnerPosition="center">
				<div className={styles.player_video__wrap} style={{ paddingTop: zoomParams.paddingTop }}>
					{isSessionInitialized && files?.url && (
						<ReactPlayer
							className={styles.player_video__inner}
							style={{ top: zoomParams.top, left: zoomParams.left }}
							url={files.url}
							width={zoomParams.width}
							height={zoomParams.height}
							playing={Boolean(playing)}
							muted={muted}
							onReady={handleReady}
							onDuration={handleDuration}
							onProgress={handleProgress}
							onSeek={reloadIfStale}
							onBuffer={() => reloadIfStale(getCurrentTime())}
							onPlay={() => null}
							onPause={() => null}
							volume={Number(volume)}
							onStart={handleStart}
							onEnded={handleComplete}
							onError={handleError}
							ref={playerRef}
							loop={false}
							config={getConfig()}
							playbackRate={Number(speed)}
						/>
					)}
				</div>
			</LoadingIndicator>
			{children}
		</div>
	);
};

export default observer(PlayerVideo);
